import React, { useState } from "react";

import { Box, Container, IconButton, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import MuiPhoneNumber from "material-ui-phone-number";
import { useTranslation } from "react-i18next";

import useBusiness from "hooks/useBusiness";
import useCart from "hooks/useCart";
import EditCircle from "icons/EditCircle";
import UserInfoIcon from "icons/UserInfoIcon";

import customerInfoValidate from "../CustomerInfo/validate";

const useStyles = makeStyles(theme => {
	return {
		container: {
			display: "flex",
			flexDirection: "column",
			padding: 0,
			paddingTop: theme.spacing(1)
		},
		titleRow: {
			marginBottom: theme.spacing(1),
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between"
		},
		titleBlock: {
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between"
		},
		titleBlockIcon: {
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between",
			marginBottom: theme.spacing(1)
		},
		userIcon: {},
		iconButton: {
			padding: 0,
			marginLeft: theme.spacing(1),
			"&:hover": {
				backgroundColor: "transparent"
			}
		},
		icon: {
			marginRight: 10,
			fill: "#fff",
			[theme.breakpoints.up(480)]: {
				fill: theme.palette.text.primary
			}
		},
		title: {
			fontSize: 18,
			textTransform: "uppercase",
			fontWeight: 500,
			color: "#fff",
			[theme.breakpoints.up(480)]: {
				color: theme.palette.text.primary
			}
		},
		change: {
			border: 0,
			padding: 0,
			fontSize: 12,
			textTransform: "uppercase",
			fontWeight: 500,
			width: "auto",
			minWidth: "auto",
			color: "#fff",
			[theme.breakpoints.up(480)]: {
				color: theme.palette.text.primary
			}
		},
		data: {
			fontSize: 12,
			textTransform: "uppercase",
			paddingBottom: theme.spacing(1)
		},
		form: {
			padding: 0
		},
		textField: {
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
			borderColor: "#fff",
			color: "#000",
			backgroundColor: "#f2f2f2",
			borderRadius: 8
		},
		phoneField: {
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
			borderColor: "#fff",
			border: "none",
			color: "#000",
			backgroundColor: "#f2f2f2",
			borderRadius: 8
		}
	};
});

interface CustomerInfoProps {
	onCustomerInfoUpdated: () => void;
	isSubmitting: boolean;
}

const CustomerInfo: React.FC<CustomerInfoProps> = ({ onCustomerInfoUpdated, isSubmitting }) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { orderType, setCustomerInfo, getCustomerInfo } = useCart();
	const { business } = useBusiness();
	const [editMode, setEditMode] = useState(true);
	const customerInfo = getCustomerInfo();
	const { fullName, phoneNumber, email } = customerInfo;

	const errors = customerInfoValidate(customerInfo);

	const onPhoneChanged = value => {
		setCustomerInfo({ ...customerInfo, phoneNumber: value });
		onCustomerInfoUpdated();
	};

	const onFullNameChange = (e: any) => {
		setCustomerInfo({ ...customerInfo, fullName: e.target.value });
		onCustomerInfoUpdated();
	};

	const onEmailChange = (e: any) => {
		setCustomerInfo({ ...customerInfo, email: e.target.value });
		onCustomerInfoUpdated();
	};

	return (
		<Container className={classes.container}>
			<Box className={classes.titleBlockIcon}>
				<UserInfoIcon className={classes.userIcon} />
			</Box>
			<Box className={classes.titleRow}>
				<Box className={classes.titleBlock}>
					<Typography className={classes.title}>{fullName ?? t("customerInformation")}</Typography>
					<IconButton className={classes.iconButton} onClick={() => setEditMode(!editMode)} title={t("edit")}>
						<EditCircle width={16} height={16} className={classes.icon} />
					</IconButton>
				</Box>
			</Box>
			{editMode ? (
				<Box className={classes.form}>
					<TextField
						className={classes.textField}
						fullWidth
						placeholder={t("fullName")}
						variant="outlined"
						inputProps={{ maxLength: 80 }}
						id="fullName"
						name="fullName"
						required
						value={fullName}
						error={isSubmitting && errors?.fullName ? true : false}
						onChange={onFullNameChange}
					/>
					{/* {orderType?.orderType !== AvailableOrderTypes.pickup ? (
						<PlacesAutocomplete
							className={classes.textField}
							placeholder={t("deliveryAddress")}
							defaultValue={customerInfo?.deliveryAddress?.place}
							origin={business?.address?.coordinates}
							radius={orderType?.deliveryRadiusMeters || undefined}
							onChange={(event, value) => {
								setCustomerInfo({
									...customerInfo,
									deliveryAddress: value && {
										address1: value.structured_formatting.main_text,
										address2: null,
										address3: null,
										city: value.structured_formatting.secondary_text,
										state: null,
										postalCode: null,
										country: null,
										placeId: value.place_id,
										place: value
									}
								});

								onCustomerInfoUpdated();
							}}
						/>
					) : null} */}
					<MuiPhoneNumber
						className={classes.phoneField}
						inputProps={{ className: classes.phoneField, placeholder: t("mobilePhone") }}
						defaultCountry={"us"}
						onlyCountries={["us"]}
						disableCountryCode
						disableDropdown
						fullWidth
						required
						id="phoneNumber"
						name="phoneNumber"
						value={phoneNumber}
						onChange={onPhoneChanged}
						error={isSubmitting && errors?.phoneNumber ? true : false}
						variant="outlined"
					/>

					<TextField
						className={classes.textField}
						fullWidth
						placeholder={t("email")}
						inputMode={"email"}
						id="email"
						name="email"
						value={email}
						onChange={onEmailChange}
						variant="outlined"
						error={isSubmitting && errors?.email ? true : false}
						inputProps={{ maxLength: 200 }}
					/>
				</Box>
			) : null}
		</Container>
	);
};

export default CustomerInfo;
